"use client";

import { useState } from "react";
import {
  Modal,
  ModalTrigger,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from "@/components/modal";
import { Button, buttonVariants } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { FloatingLabelInput } from "@/components/ui/floating-label";
import { useAuth } from "@/context/AuthContext";

const otpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, "OTP must be a 6-digit number"),
  newPassword: z.string().min(8, "Password must be at least 8 characters long"),
});

export default function ChangePasswordModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const { toast } = useToast();
  const { accessToken } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
  });

  async function requestChangePassword() {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/request-change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((res) => res.json());

      if (response.message !== "OK") {
        return toast({
          title: "Error",
          description: response.message,
          variant: "destructive",
        });
      }
      toast({
        title: "OTP Sent",
        description: "Password change OTP has been sent to your email.",
      });

      setStep(2);
    } catch (error) {
      console.error(error);

      toast({
        title: "Error",
        description: "Failed to send password change OTP.",
        variant: "destructive",
      });
    }
  }

  async function changePassword(data: { otp: string; newPassword: string }) {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            code: Number(data.otp),
            password: data.newPassword,
          }),
        },
      ).then((res) => res.json());

      if (response.message !== "OK") {
        // Handle validation errors from API
        if (response.errors?.password?.param?.msg) {
          return toast({
            title: "Validation Error",
            description: response.errors.password.param.msg,
            variant: "destructive",
          });
        }

        return toast({
          title: "Error",
          description: response.message || "Failed to change password",
          variant: "destructive",
        });
      }

      toast({
        title: "Password Changed",
        description: "Your password has been successfully changed.",
      });

      setStep(1);
      setOpen(false);
    } catch (error) {
      console.error(error);

      toast({
        title: "Error",
        description: "Failed to change password.",
        variant: "destructive",
      });
    }
  }

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger className={buttonVariants({ variant: "secondary" })}>
        Change Password
      </ModalTrigger>
      <ModalContent className="max-w-[17rem]">
        <ModalHeader>
          <ModalTitle>Change Password</ModalTitle>
          <ModalDescription>
            {step === 1
              ? "Request password change OTP"
              : "Enter the OTP and your new password"}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {step === 1 && (
            <Button onClick={requestChangePassword}>
              Request Password Change OTP
            </Button>
          )}
          {step === 2 && (
            <form
              onSubmit={handleSubmit(changePassword)}
              className="flex flex-col items-center justify-center space-y-4"
            >
              <div className="flex flex-col items-start space-y-2">
                <Controller
                  control={control}
                  name="otp"
                  render={({ field }) => (
                    <InputOTP
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
                {errors.otp && (
                  <p className="text-sm text-destructive">
                    {errors.otp.message}
                  </p>
                )}
              </div>

              <div className="flex w-full flex-col space-y-2">
                <FloatingLabelInput
                  type="password"
                  id="newPassword"
                  label="New Password"
                  {...register("newPassword")}
                />
                {errors.newPassword && (
                  <p className="text-sm text-destructive">
                    {errors.newPassword.message}
                  </p>
                )}
              </div>

              <Button type="submit">Change Password</Button>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
